import React from 'react'
import Modal, { ModalHeader, ModalBody, ModalFooter } from 'core-system/Modal'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import Text from 'core-system/Text/Text'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'
import palette from 'core-system/Themes/palette'

const ModalContainer = styled(Modal)`
  width: 30rem;
`

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  text-align: left;
  padding: 0.5rem 0;
`

const employerText = `Before we get started, you’ll need to complete the onboarding tasks shown in the table above. These tasks are designed to properly set up your commuter benefits program.

As you complete each item, new tasks may appear. Some tasks need to be completed in order, which is why you might see some marked as “Blocked.”

Once you’ve finished all onboarding items, you’ll be able to launch your program and invite your employees to access their commuter benefits!

Need help? Reach out to us at support@movewithfleet.com at any time.
`

const SUPPORT_EMAIL = 'support@movewithfleet.com'

interface WelcomeModalProps {
  open: boolean
  onClose: (e: React.MouseEvent<any>) => void
  type: 'employee' | 'employer'
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({
  open,
  onClose,
  type = 'employer',
}) => {
  const navigate = useNavigate()
  const modalRef = React.useRef<HTMLDivElement>(null)
  const lastActiveElementRef = React.useRef<HTMLElement | null>(null)

  const handleContinue = () => {
    // Keep overall flow the same: dismiss modal and return focus to dashboard.
    // For employer onboarding, we keep the previous navigation behavior.
    if (type === 'employer') {
      navigate(Locations.Dashboard)
    }
    onClose({} as React.MouseEvent<any>)
  }

  React.useEffect(() => {
    if (!open) return

    // Move focus into the modal on open
    lastActiveElementRef.current = document.activeElement as HTMLElement | null

    const titleEl = document.getElementById(
      'welcome-modal-title'
    ) as HTMLElement | null
    titleEl?.focus?.()

    // Trap focus inside the modal
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const root = modalRef.current
      if (!root) return

      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1)

      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (e.shiftKey) {
        if (!active || active === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)

      // Restore focus on close
      const prev = lastActiveElementRef.current
      if (prev && typeof prev.focus === 'function') {
        prev.focus()
      } else {
        // Fallback: focus something sensible on the dashboard
        const fallback = document.querySelector(
          '#page-main-body [data-cy], #page-main-body h1, #page-main-body h2, #page-main-body'
        ) as HTMLElement | null
        fallback?.focus?.()
      }
    }
  }, [open])

  return (
    <ModalContainer
      ref={modalRef}
      open={open}
      onClose={onClose}
      role='dialog'
      aria-modal='true'
      aria-labelledby='welcome-modal-title'
    >
      <ModalHeader
        id='welcome-modal-title'
        tabIndex={-1}
        title={
          type === 'employee' ? 'Welcome to Fleet 👋' : 'Welcome to Fleet!'
        }
        onClose={onClose}
      />
      <ModalBody>
        <WelcomeContainer>
          {type === 'employer' ? (
            <Text variant='body1' preWrap>
              {employerText}
            </Text>
          ) : (
            <>
              <Text variant='body1' fontWeight={600} marginBottom='0.5rem'>
                Let’s activate your commuter benefits.
              </Text>
              <Text
                variant='action3'
                color={palette.text.secondary}
                marginBottom='0.5rem'
              >
                Complete a few quick setup steps to start using your Fleet card.
              </Text>
              <Text
                variant='action3'
                color={palette.text.secondary}
                marginBottom='0.75rem'
              >
                You’ll find your onboarding checklist on the dashboard.
              </Text>
              <Text variant='action4' color={palette.text.secondary}>
                Need help? Email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </Text>
            </>
          )}
        </WelcomeContainer>
      </ModalBody>
      <ModalFooter>
        <PrimaryButton fullWidth onClick={handleContinue}>
          Continue
        </PrimaryButton>
      </ModalFooter>
    </ModalContainer>
  )
}

export default WelcomeModal
